/**
 * Guitar Amp Simulator Component
 * Professional amp modeling with cabinet simulation and pedal chain
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Guitar,
  Volume2,
  Zap,
  Waves,
  Speaker,
  Settings,
  Power,
  RotateCcw,
  ChevronDown,
  Music,
  Flame,
  Wind,
  Sparkles,
} from 'lucide-react';

// Amp Models with characteristics
const AMP_MODELS = {
  clean: {
    name: 'Clean Twin',
    description: 'Crystal clear Fender-style clean',
    icon: Sparkles,
    color: 'from-cyan-500 to-blue-500',
    defaults: { gain: 30, bass: 50, mid: 50, treble: 55, presence: 45, master: 60 },
  },
  crunch: {
    name: 'British Crunch',
    description: 'Classic Marshall-style breakup',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    defaults: { gain: 55, bass: 45, mid: 60, treble: 55, presence: 50, master: 55 },
  },
  highGain: {
    name: 'High Gain',
    description: 'Modern Mesa-style saturation',
    icon: Flame,
    color: 'from-red-500 to-rose-600',
    defaults: { gain: 75, bass: 55, mid: 45, treble: 60, presence: 55, master: 50 },
  },
  metal: {
    name: 'Metal Machine',
    description: 'Extreme gain for heavy tones',
    icon: Guitar,
    color: 'from-violet-600 to-purple-600',
    defaults: { gain: 90, bass: 60, mid: 35, treble: 65, presence: 60, master: 45 },
  },
  acoustic: {
    name: 'Acoustic Sim',
    description: 'Acoustic guitar simulation',
    icon: Music,
    color: 'from-emerald-500 to-teal-500',
    defaults: { gain: 20, bass: 45, mid: 55, treble: 60, presence: 40, master: 70 },
  },
  // NEW AMP MODELS
  britishStack: {
    name: 'British Stack',
    description: 'Legendary Plexi tone with singing sustain',
    icon: Zap,
    color: 'from-yellow-500 to-amber-600',
    defaults: { gain: 65, bass: 50, mid: 65, treble: 60, presence: 55, master: 50 },
  },
  vintageDeluxe: {
    name: 'Vintage Deluxe',
    description: 'Blackface-era warm cleans and edge-of-breakup',
    icon: Sparkles,
    color: 'from-sky-400 to-cyan-500',
    defaults: { gain: 40, bass: 55, mid: 45, treble: 50, presence: 50, master: 65 },
  },
  modernMetal: {
    name: 'Modern Metal',
    description: 'Djent-ready tight low end with scooped mids',
    icon: Flame,
    color: 'from-zinc-600 to-slate-800',
    defaults: { gain: 85, bass: 70, mid: 25, treble: 70, presence: 65, master: 45 },
  },
  bassDriver: {
    name: 'Bass Driver',
    description: 'Optimized for bass guitar with deep lows',
    icon: Volume2,
    color: 'from-indigo-500 to-blue-600',
    defaults: { gain: 45, bass: 75, mid: 40, treble: 35, presence: 30, master: 70 },
  },
  jazzClean: {
    name: 'Jazz Clean',
    description: 'Warm, round tone perfect for jazz',
    icon: Music,
    color: 'from-amber-400 to-yellow-500',
    defaults: { gain: 25, bass: 60, mid: 55, treble: 40, presence: 35, master: 70 },
  },
  bluesBreaker: {
    name: 'Blues Breaker',
    description: 'Touch-sensitive blues tone with natural breakup',
    icon: Wind,
    color: 'from-blue-500 to-indigo-500',
    defaults: { gain: 50, bass: 55, mid: 55, treble: 50, presence: 45, master: 60 },
  },
  matchless: {
    name: 'Boutique AC',
    description: 'Chimey AC-style with class-A warmth',
    icon: Sparkles,
    color: 'from-rose-400 to-pink-500',
    defaults: { gain: 45, bass: 40, mid: 60, treble: 65, presence: 55, master: 55 },
  },
  dualRecto: {
    name: 'Dual Rectifier',
    description: 'Massive low-end with aggressive mids',
    icon: Flame,
    color: 'from-orange-600 to-red-700',
    defaults: { gain: 80, bass: 65, mid: 50, treble: 55, presence: 50, master: 50 },
  },
};

// Cabinet Models
const CABINET_MODELS = {
  '1x12': { name: '1x12 Open Back', size: 'small', character: 'Bright & focused' },
  '2x12': { name: '2x12 Combo', size: 'medium', character: 'Balanced & warm' },
  '4x12': { name: '4x12 Closed', size: 'large', character: 'Full & punchy' },
  '4x12v30': { name: '4x12 V30', size: 'large', character: 'Modern & aggressive' },
  direct: { name: 'Direct (No Cab)', size: 'none', character: 'Raw amp tone' },
};

// Microphone positions for cab
const MIC_POSITIONS = {
  center: { name: 'Center Cap', brightness: 100 },
  edge: { name: 'Edge of Cone', brightness: 60 },
  offAxis: { name: 'Off-Axis', brightness: 40 },
  room: { name: 'Room Blend', brightness: 70 },
};

// Knob component for amp controls
const AmpKnob = ({ label, value, onChange, min = 0, max = 100, color = 'violet' }) => {
  const rotation = ((value - min) / (max - min)) * 270 - 135;
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div 
        className="relative w-12 h-12 cursor-pointer group"
        onMouseDown={(e) => {
          const startY = e.clientY;
          const startValue = value;
          
          const handleMove = (moveEvent) => {
            const delta = (startY - moveEvent.clientY) * 0.5;
            const newValue = Math.max(min, Math.min(max, startValue + delta));
            onChange(Math.round(newValue));
          };
          
          const handleUp = () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
          };
          
          document.addEventListener('mousemove', handleMove);
          document.addEventListener('mouseup', handleUp);
        }}
      >
        <div className={`absolute inset-0 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 border-2 border-zinc-600 shadow-lg group-hover:border-${color}-500/50 transition-colors`}>
          <div 
            className="absolute top-1/2 left-1/2 w-0.5 h-4 bg-white rounded-full origin-bottom"
            style={{ transform: `translate(-50%, -100%) rotate(${rotation}deg)` }}
          />
        </div>
        <div className={`absolute -inset-1 rounded-full bg-${color}-500/20 opacity-0 group-hover:opacity-100 transition-opacity blur`} />
      </div>
      <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      <span className="text-xs text-white font-mono">{value}</span>
    </div>
  );
};

// LED indicator
const LEDIndicator = ({ active, color = 'emerald' }) => (
  <div className={`w-2 h-2 rounded-full ${active ? `bg-${color}-500 shadow-lg shadow-${color}-500/50` : 'bg-zinc-700'} transition-all`} />
);

export const GuitarAmpSimulator = ({ 
  trackId,
  settings = {},
  onChange,
  onBypass,
  isBypassed = false,
}) => {
  const [ampModel, setAmpModel] = useState(settings.ampModel || 'crunch');
  const [cabinet, setCabinet] = useState(settings.cabinet || '4x12');
  const [micPosition, setMicPosition] = useState(settings.micPosition || 'center');
  const [controls, setControls] = useState(settings.controls || AMP_MODELS.crunch.defaults);
  const [showCabSettings, setShowCabSettings] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  
  // Update parent when settings change
  useEffect(() => {
    onChange?.({
      ampModel,
      cabinet,
      micPosition,
      controls,
    });
  }, [ampModel, cabinet, micPosition, controls, onChange]);
  
  // Handle amp model change
  const handleAmpChange = useCallback((model) => {
    setAmpModel(model);
    setControls(AMP_MODELS[model].defaults);
  }, []);
  
  // Handle control change
  const handleControlChange = useCallback((param, value) => {
    setControls(prev => ({ ...prev, [param]: value }));
  }, []);
  
  // Reset to defaults
  const handleReset = useCallback(() => {
    setControls(AMP_MODELS[ampModel].defaults);
  }, [ampModel]);
  
  const currentAmp = AMP_MODELS[ampModel];
  const AmpIcon = currentAmp.icon;
  
  return (
    <div className={`bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 rounded-xl border ${isBypassed ? 'border-zinc-700 opacity-60' : 'border-zinc-700'} overflow-hidden`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/50 border-b border-zinc-700/50">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currentAmp.color} flex items-center justify-center`}>
            <AmpIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">Guitar Amp</h3>
            <p className="text-xs text-zinc-400">{currentAmp.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
            title="Reset to defaults"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant={isBypassed ? 'outline' : 'default'}
            size="sm"
            onClick={() => onBypass?.(!isBypassed)}
            className={`h-7 px-2 ${!isBypassed ? 'bg-emerald-600 hover:bg-emerald-500' : ''}`}
          >
            <Power className="w-3.5 h-3.5 mr-1" />
            {isBypassed ? 'OFF' : 'ON'}
          </Button>
        </div>
      </div>
      
      {/* Amp Model Selector */}
      <div className="px-4 py-3 border-b border-zinc-700/50">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {Object.entries(AMP_MODELS).map(([key, amp]) => {
            const Icon = amp.icon;
            return (
              <button
                key={key}
                onClick={() => handleAmpChange(key)}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  ampModel === key
                    ? `bg-gradient-to-r ${amp.color} border-transparent text-white`
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs font-medium whitespace-nowrap">{amp.name}</span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Amp Controls */}
      <div className="p-4">
        <div className="grid grid-cols-6 gap-4 justify-items-center mb-4">
          <AmpKnob label="Gain" value={controls.gain} onChange={(v) => handleControlChange('gain', v)} color="orange" />
          <AmpKnob label="Bass" value={controls.bass} onChange={(v) => handleControlChange('bass', v)} color="blue" />
          <AmpKnob label="Mid" value={controls.mid} onChange={(v) => handleControlChange('mid', v)} color="emerald" />
          <AmpKnob label="Treble" value={controls.treble} onChange={(v) => handleControlChange('treble', v)} color="yellow" />
          <AmpKnob label="Presence" value={controls.presence} onChange={(v) => handleControlChange('presence', v)} color="cyan" />
          <AmpKnob label="Master" value={controls.master} onChange={(v) => handleControlChange('master', v)} color="violet" />
        </div>
        
        {/* Input/Output meters */}
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 uppercase">Input</span>
            <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-yellow-500" style={{ width: `${inputLevel}%` }} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-violet-500" style={{ width: `${Math.min(100, inputLevel * (controls.gain / 50))}%` }} />
            </div>
            <span className="text-[10px] text-zinc-500 uppercase">Output</span>
          </div>
        </div>
      </div>
      
      {/* Cabinet Section */}
      <div className="border-t border-zinc-700/50">
        <button
          onClick={() => setShowCabSettings(!showCabSettings)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Speaker className="w-4 h-4 text-amber-400" />
            <div className="text-left">
              <span className="text-sm font-medium text-white">{CABINET_MODELS[cabinet].name}</span>
              <span className="text-xs text-zinc-500 ml-2">• {MIC_POSITIONS[micPosition].name}</span>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${showCabSettings ? 'rotate-180' : ''}`} />
        </button>
        
        <AnimatePresence>
          {showCabSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                {/* Cabinet selection */}
                <div>
                  <label className="text-xs text-zinc-400 mb-2 block">Cabinet</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CABINET_MODELS).map(([key, cab]) => (
                      <button
                        key={key}
                        onClick={() => setCabinet(key)}
                        className={`px-3 py-2 rounded-lg text-xs transition-all ${
                          cabinet === key
                            ? 'bg-amber-500/20 border border-amber-500/50 text-amber-400'
                            : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:text-white'
                        }`}
                      >
                        {cab.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Mic position */}
                {cabinet !== 'direct' && (
                  <div>
                    <label className="text-xs text-zinc-400 mb-2 block">Mic Position</label>
                    <div className="grid grid-cols-4 gap-2">
                      {Object.entries(MIC_POSITIONS).map(([key, mic]) => (
                        <button
                          key={key}
                          onClick={() => setMicPosition(key)}
                          className={`px-2 py-1.5 rounded text-[10px] transition-all ${
                            micPosition === key
                              ? 'bg-violet-500/20 border border-violet-500/50 text-violet-400'
                              : 'bg-zinc-800/50 border border-zinc-700 text-zinc-400 hover:text-white'
                          }`}
                        >
                          {mic.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GuitarAmpSimulator;
